import { getProductContractsOfCertainPOTType } from '@/utils/dataHandlers/ContractsDataHandler';
import {
  getProductByPOId,
  hasAnyProductsWithMobileRent2RentContracted,
  isRent2RentByProductId,
} from '@/utils/dataHandlers/ProductDataHandler';
import {
  changeTelcoFormatToFrontFormat,
  getTimeDifferenceInYears,
} from '@/utils/momentFacilities';
import {
  FUSION,
  SPECIFIC_CUSTOMER_PRODUCT_KINDS,
  SPECIFIC_CUSTOMER_PRODUCT_OPTIONS,
  SPECIFIC_CUSTOMER_PRODUCT_POSITIONS,
  rent2rent,
  PERMANENCE,
  ONLY_MOBILE,
  MOBILE_PREPAID,
} from '@/constants/constants';
import linkProductsJSON from '@/constants/linkProductsJSON.json';
const publicContractsUrl = 'https://www.movistar.es/contratos';
import { ProductInfoTexts } from '../../constants/textConstants';
import { getUrl } from '../commonUtils';
import { concatUrl } from '../htmlUtils';
import { TEXT_TYPES_CODES } from '@/constants/catalogue';

export const normalizeBase64ImageToCss = (image, type) => {
  let normalizedImage = '';
  if (image) {
    normalizedImage = `data:image/${type};base64,${image}`;
  }
  return normalizedImage;
};

export const normalizeCatalogPrice = (price) => {
  let normalizedPrice = '';
  let hasVariablePrice = false;
  if (price) {
    const hasAsterisk = price.indexOf('*');
    if (hasAsterisk !== -1) {
      normalizedPrice = price.replace('*', '');
      hasVariablePrice = true;
    } else {
      normalizedPrice = price;
    }
  }
  return { price: normalizedPrice, hasVariablePrice };
};

export const getJsonLinkObj = (product) => {
  let jsonObj = {};
  if (product) {
    if (product.productType === FUSION) {
      jsonObj = linkProductsJSON.fusion;
    } else if (product.productType === ONLY_MOBILE) {
      jsonObj = linkProductsJSON.movilContrato;
    } else if (product.productType === MOBILE_PREPAID) {
      jsonObj = linkProductsJSON.movilPrepago;
    } else {
      jsonObj = linkProductsJSON[product.poId] || {};
    }
  }
  const {
    productUrl,
    manageUrl,
    changePlan,
    userManualURL,
    quickGuideURL,
    buyUrl,
    cancelUrl,
  } = jsonObj;
  return {
    productUrl: productUrl
      ? concatUrl(productUrl, 'pid=clic-acceso-ficha-tcol')
      : '',
    manageUrl: manageUrl || '',
    changePlan: changePlan
      ? concatUrl(changePlan, 'pid=area-privada-tcol')
      : '',
    userManualURL: userManualURL || '',
    quickGuideURL: quickGuideURL || '',
    buyUrl: buyUrl ? concatUrl(buyUrl, 'pid=clic-activacion-tcol') : '',
    cancelUrl: cancelUrl || '',
  };
};

export const getCatalogSubSectionByPOId = (catalogueSubsectionsInfo, poId) => {
  let result = null;
  if (catalogueSubsectionsInfo) {
    const catalogueSubSections = Object.values(catalogueSubsectionsInfo);
    const subSectionFound = catalogueSubSections.find(
      (x) =>
        x.productOfferings &&
        Array.isArray(x.productOfferings) &&
        x.productOfferings.some((y) => y.id === poId)
    );
    if (subSectionFound) {
      result = subSectionFound;
    }
  }
  return result;
};

export const getCustomerProductSpecificKindInfo = (
  poId,
  product,
  hiredProducts,
  isR2RAvailableToPurchase
) => {
  let kindInfo = {
    kind: SPECIFIC_CUSTOMER_PRODUCT_OPTIONS.DEFAULT,
    position: SPECIFIC_CUSTOMER_PRODUCT_POSITIONS.TOP,
  };
  let specificKindInfoKey = Object.keys(SPECIFIC_CUSTOMER_PRODUCT_KINDS).find(
    (key) =>
      SPECIFIC_CUSTOMER_PRODUCT_KINDS[key].prodOfferList.find(
        (po) => po === poId
      )
  );
  if (!specificKindInfoKey) {
    if (
      product &&
      product.categorias &&
      (product.categorias.tipoDispositivo === rent2rent.devicesTypeR2R ||
        product.categorias.tipoDispositivo ===
          rent2rent.devicesTypeAdditionalR2R)
    ) {
      specificKindInfoKey = 'MOBILE_RENT2RENT';
    }
  }
  if (!specificKindInfoKey && product && product.productType === FUSION) {
    // const { mobileRent2Rent, mobileAdditionalRent2Rent } = isFusionWithMobileRent2RentContracted(product);
    const { hasMobileRent2Rent, hasMobileAddiotionalRent2Rent } =
      hasAnyProductsWithMobileRent2RentContracted(hiredProducts);
    const { isRent2Rent, isRent2RentAdditional } = isRent2RentByProductId(
      isR2RAvailableToPurchase
    );
    if (
      hasMobileRent2Rent ||
      hasMobileAddiotionalRent2Rent ||
      isRent2Rent ||
      isRent2RentAdditional
    ) {
      specificKindInfoKey = 'FUSION_WITH_MOBILE_RENT2RENT';
    }
  }
  if (specificKindInfoKey) {
    const { kind, position } =
      SPECIFIC_CUSTOMER_PRODUCT_KINDS[specificKindInfoKey];
    kindInfo = { kind, position };
  }
  return kindInfo;
};

export const getProductOfferingAttachmentByType = (
  productOfferingStructure,
  attachmentType
) => {
  return getProductOfferingAttachmentByAttachmentType(
    productOfferingStructure,
    attachmentType,
    'telcol'
  );
};

export const getProductOfferingImageAttachment = (catalogAttachmentsInfo) => {
  let imageAttach = getProductOfferingAttachmentByType(
    catalogAttachmentsInfo,
    "ATTACHMENTS_TYPES.IMG_HIGH_RESOLUTION"
  );
  if (!imageAttach) {
    imageAttach = getProductOfferingAttachmentByType(
      catalogAttachmentsInfo,
      "ATTACHMENTS_TYPES.IMG_MEDIUM_RESOLUTION"
    );
  }
  if (!imageAttach) {
    imageAttach = getProductOfferingAttachmentByType(
      catalogAttachmentsInfo,
      "ATTACHMENTS_TYPES.ICON_EXTENDED"
    );
  }
  return normalizeBase64ImageToCss(imageAttach);
};

export const getCustomerProductInfo = (
  catalogProdOfferInfo,
  catalogAttachmentsInfo,
  billingDateInfo,
  product,
  contractsInfo,
  hiredProducts,
  urlLibrary,
) => {
  const title = getProductOfferingTextByType(
    catalogProdOfferInfo,
    "TEXT_TYPES_CODES.ORDINARY"
  );
  const subtitle = getProductOfferingTextByType(
    catalogProdOfferInfo,
    "TEXT_TYPES_CODES.SHORT_EXPLANATORY"
  );
  let contractualInfo = null;
  let promotionalInfo = null;
  let promotionalText = '';
  let promotionEndDate = '';
  let commitmentEndDate = '';
  let hasContractToDownload = false;
  if (contractsInfo) {
    const { contracts } = contractsInfo;
    if (contracts && Array.isArray(contracts)) {
      const commitmentContracts = getProductContractsOfCertainPOTType(
        contracts,
        PERMANENCE
      );
      // TODO ¿Pueden tener varios contratos de permanencia?
      const [contract] = commitmentContracts;
      if (contract) {
        commitmentEndDate = contract && contract[0] && contract[0].fechaFin;
      }
      const { attachments } = contracts;
      if (attachments && Array.isArray(attachments) && attachments.length > 0) {
        hasContractToDownload = true;
      }
    }
  }
  const { productId, promotions, startDate, endDate } = product;
  let startDateFormatted = '';
  if (startDate) {
    startDateFormatted = changeTelcoFormatToFrontFormat(startDate);
  }
  let endDateFormatted = '';
  if (endDate && getTimeDifferenceInYears(endDate) <= 100) {
    endDateFormatted = changeTelcoFormatToFrontFormat(endDate);
  }
  contractualInfo = {
    productBeginDate: startDateFormatted,
    productEndDate: endDateFormatted,
    commitmentEndDate,
  };

  const contractsUrlToShow =
    hasContractToDownload || commitmentEndDate
      ? getUrl('myContracts',urlLibrary)
      : publicContractsUrl;

  if (promotions && Array.isArray(promotions) && promotions.length === 1) {
    const [promoInfo] = promotions;
    const { fechaFin, expire, programmed } = promoInfo;
    promotionalText = getProductPromotionTextByType(
      promoInfo,
      "TEXT_TYPES_CODES.BRIEF"
    );
    if (fechaFin) {
      promotionEndDate = changeTelcoFormatToFrontFormat(fechaFin);
    }
    if (promotionalText) {
      promotionalInfo = {
        text: promotionalText,
        endDate: promotionEndDate,
        expire,
        programmed,
      };
    }
  }

  const { billingDate } = { ...billingDateInfo };

  const productPriceInfo = getProductOfferingTextByType(
    catalogProdOfferInfo,
    "TEXT_TYPES_CODES.SHORT"
  );

  const { hasVariablePrice, price } = normalizeCatalogPrice(productPriceInfo);

  let pricePrefix = '';
  if (promotionalText || hasVariablePrice) {
    ({ pricePrefix } = ProductInfoTexts);
  }
  const billingInfo = {
    nextBillDate: billingDate
      ? "moment.changeServerFormatToFrontFormat(billingDate)"
      : null,
    pricePrefix,
    productPrice: price,
  };

  let imageAttach = getProductOfferingAttachmentByType(
    catalogAttachmentsInfo,
    "ATTACHMENTS_TYPES.IMG_HIGH_RESOLUTION"
  );
  if (!imageAttach) {
    imageAttach = getProductOfferingAttachmentByType(
      catalogAttachmentsInfo,
      "ATTACHMENTS_TYPES.ICON_EXTENDED"
    );
  }

  const {
    manageUrl,
    changePlan,
    userManualURL,
    quickGuideURL,
    buyUrl,
    cancelUrl,
  } = getJsonLinkObj(product);

  const { content } = { ...imageAttach };

  let hasFusion = null;
  let changePlanWithParams = changePlan;
  if (product && product.productType === ONLY_MOBILE && changePlan) {
    hasFusion = hiredProducts.find(
      (x) => x.productType === "PRODUCT_TYPES_TCOL.FUSION"
    );
    changePlanWithParams = concatUrl(
      changePlanWithParams,
      `productId=${product.productId}`
    );
    changePlanWithParams = concatUrl(
      changePlanWithParams,
      `productOffering=${product.poId}`
    );
    if (hasFusion) {
      changePlanWithParams = concatUrl(changePlanWithParams, 'filter=fusion');
    } else
      changePlanWithParams = concatUrl(changePlanWithParams, 'filter=todas');
  }

  return {
    title,
    subtitle,
    productId,
    contractualInfo,
    billingInfo,
    promotionalInfo,
    manageServiceUrl: manageUrl,
    image: normalizeBase64ImageToCss(content),
    changePlanUrl: changePlanWithParams,
    userManualURL,
    quickGuideURL,
    contractsUrlToShow,
    buyUrl,
    cancelUrl,
    hasContractToDownload,
  };
};

export const getDetailProductInfo = (
  catalogProdOfferInfo,
  catalogAttachmentsInfo,
  selectedProduct
) => {
  const title = getProductOfferingTextByType(
    catalogProdOfferInfo,
    "TEXT_TYPES_CODES.ORDINARY"
  );

  const subtitle = getProductOfferingTextByType(
    catalogProdOfferInfo,
    "TEXT_TYPES_CODES.SHORT_EXPLANATORY"
  );

  const description = getProductOfferingTextByType(
    catalogProdOfferInfo,
    "TEXT_TYPES_CODES.EXPLANATORY"
  );

  const { manageUrl } = getJsonLinkObj(selectedProduct);

  let imageAttach = getProductOfferingAttachmentByType(
    catalogAttachmentsInfo,
    "ATTACHMENTS_TYPES.IMG_HIGH_RESOLUTION"
  );
  if (!imageAttach) {
    imageAttach = getProductOfferingAttachmentByType(
      catalogAttachmentsInfo,
      "ATTACHMENTS_TYPES.ICON_EXTENDED"
    );
  }

  const { content } = { ...imageAttach };

  return {
    title,
    subtitle,
    description,
    manageUrl,
    image: normalizeBase64ImageToCss(content),
  };
};


export const getProductNameOfferingByType = ({catalogueProductOffering, textType, poId}) => {
  let texts = null;
  if (catalogueProductOffering && catalogueProductOffering[poId] && catalogueProductOffering[poId].texts) {
    texts = catalogueProductOffering[poId].texts.filter((text) => text.typeId === textType);
  }
  return texts
};

export const getProductOfferingTextByType = (catalogueProductOffering, textType) => {
  let text = null;
  if (catalogueProductOffering && Array.isArray(catalogueProductOffering.texts) && catalogueProductOffering.texts.length > 0) {
    const textTemp = catalogueProductOffering.texts.find((text) => text.typeId === textType);
    if (textTemp) {
      ({ text } = textTemp)
    }
  }
  return text;
};

export const getTitleCatalogueProductOffering = ({ catalogueProductOffering, poId }) => {
  if (catalogueProductOffering && catalogueProductOffering[poId] && catalogueProductOffering[poId].texts) {
    const texts = catalogueProductOffering[poId].texts;
    const title = texts.filter((text) => text.typeId === TEXT_TYPES_CODES.ORDINARY);
    if (title && title[0] && title[0].text) {
      return title[0].text;
    }
  } 
}


export const getFttrTexts = ({ catalogTexts }) => {
  if (catalogTexts && catalogTexts.texts && Array.isArray(catalogTexts.texts) && catalogTexts.texts.length > 0) {
    const id = catalogTexts?.id;
    const findTexts = catalogTexts.texts.find((item) => item?.typeId === TEXT_TYPES_CODES.EXPLANATORY);

    if (findTexts) {
      const literals = findTexts?.completeText?.[0]?.literals;
      if (Array.isArray(literals) && literals.length > 0) {
        const title = literals[0].split("|")[1].trim();
        const description = literals.slice(1).join(" ");

        return { id, title, description }
      }
    }
  }
}